import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { FiDownload, FiFilter, FiCalendar, FiRefreshCw, FiCreditCard, FiXCircle } from 'react-icons/fi';
import { Card, Button, Row, Col, Spinner, Alert, Form, OverlayTrigger, Tooltip, Dropdown, DropdownButton } from 'react-bootstrap';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import LedgerDetailsOffcanvas from './LedgerDetailsOffcanvas';

const Balancesheet = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedLedger, setSelectedLedger] = useState(null);
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [dateRangeFilter, setDateRangeFilter] = useState('yearly');
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedEndYear, setSelectedEndYear] = useState(null);

    const handleDateRangeChange = (value) => {
        setDateRangeFilter(value);
        if (value === 'yearly') {
            setSelectedYear(null);
            setSelectedEndYear(null);
            setStartDate('');
            setEndDate('');
        }
    };

    const Api = process.env.REACT_APP_WEBZASH_URL;
    const apiKey = process.env.REACT_APP_API_KEY;

    const fetchBankAccounts = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });
            setBankAccounts(response.data.account || []);
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchBalanceSheet = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {};
            if (startDate) params.startdate = startDate;
            if (endDate) params.enddate = endDate;

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const res = await axios.get(`${backendUrl}/api/balance-sheet`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Accept': 'application/json'
                },
                params,
                skipLoader: true
            });
            
            console.log("=== BALANCE SHEET API RESPONSE ===");
            console.log("Raw Response Data:", res.data);
            console.log("Parsed Data:", res.data.data);
            
            let responseData = res.data.data;
            if (responseData && responseData.liabilities) {
                responseData.liabilities = responseData.liabilities.map(group => {
                    // Match "Capital Account" group
                    if (group.name && group.name.toLowerCase().includes('capital account')) {
                        const drawingsIndex = group.children.findIndex(ledger =>
                            ledger.name && ledger.name.toLowerCase().includes('drawing')
                        );
                        if (drawingsIndex !== -1) {
                            const drawingsLedger = group.children[drawingsIndex];
                            const newChildren = [...group.children];
                            newChildren.splice(drawingsIndex, 1);
                            newChildren.push(drawingsLedger);
                            return { ...group, children: newChildren };
                        }
                    }
                    return group;
                });
            }
            setData(responseData);
        } catch (err) {
            console.error('Error fetching balance sheet:', err);
            setError('Failed to fetch balance sheet data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadFile = async (format, expanded = false) => {
        try {
            if (format === 'pdf') {
                // Call local backend for PDF export
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const params = {
                    startdate: startDate,
                    enddate: endDate,
                    expanded: expanded,
                    format: 'pdf'
                };

                const res = await axios.get(`${backendUrl}/api/reports/balance-sheet`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Accept': 'application/pdf'
                    },
                    params,
                    responseType: 'blob'
                });

                const blob = new Blob([res.data], { type: 'application/pdf' });
                saveAs(blob, `BalanceSheet_${startDate || 'All'}_to_${endDate || 'All'}.pdf`);
            } else {
                // Keep CSV/Excel for ERP Backend API
                const params = {
                    startdate: startDate,
                    enddate: endDate,
                    [`download${format}`]: 1
                };

                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const res = await axios.get(`${backendUrl}/api/balance-sheet`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Accept': 'application/octet-stream'
                    },
                    params,
                    responseType: 'blob',
                    skipLoader: true
                });

                const blob = new Blob([res.data], { type: res.headers['content-type'] });
                const extension = format === 'xls' ? 'xls' : 'csv';
                saveAs(blob, `BalanceSheet_${startDate}_to_${endDate}.${extension}`);
            }
        } catch (err) {
            console.error(`Error downloading ${format.toUpperCase()} file:`, err);
            setError(`Failed to download ${format.toUpperCase()} file. Please try again.`);
        }
    };

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };
    const handleClearFilters = () => {
        setStartDate('');
        setEndDate('');
        setSelectedYear(null);
        setSelectedEndYear(null);
        setDateRangeFilter('yearly');
        fetchBalanceSheet(); // Re-fetch without filters
    };

    const formatAmountWithType = (amount, dc) => {
        const numericAmount = parseFloat(amount);
        const formattedAmount = formatCurrency(amount);

        // Don't show Dr/Cr for zero amounts
        if (numericAmount === 0) {
            return formattedAmount;
        }

        const type = dc === 'D' ? 'Dr' : 'Cr';
        return `${formattedAmount} ${type}`;
    };

    const getAmountClass = (dc) => {
        return dc === 'D' ? 'text-danger' : 'text-success';
    };

    const getTypeText = (dc) => {
        return dc === 'D' ? 'Dr' : 'Cr';
    };
    const getDayjsDate = (dateString) => {
        return dateString ? dayjs(dateString) : null;
    };

    const handleLedgerClick = (item, parentName = '') => {
        const isGroup = item.children && item.children.length > 0;
        setSelectedLedger({ ...item, group_name: parentName, is_group: isGroup });
        setShowOffcanvas(true);
    };

    const RenderAccount = ({ item, level = 0, parentName = '' }) => {
        if (!item) return null;
        const isGroup = item.children && item.children.length > 0;

        return (
            <div className={`mb-${level === 0 ? '4' : '2'}`}>
                {/* Header or Row */}
                <div
                    className={`d-flex justify-content-between ${level === 0 ? 'border-bottom pb-2 mb-2' : 'py-1'}`}
                    onClick={() => !isGroup && handleLedgerClick(item, parentName)}
                    style={{ cursor: isGroup ? 'default' : 'pointer' }}
                    title={isGroup ? "" : "Click for details"}
                >
                    {level === 0 ? (
                        <strong>{item.name}</strong>
                    ) : (
                        <span className={!isGroup ? "text-dark" : ""}>{item.name}</span>
                    )}

                     {!isGroup && (() => {
                        const bank = bankAccounts.find(b => b.name?.trim().toLowerCase() === item.name?.trim().toLowerCase());
                        let usdTooltipContent = null;

                        if (bank) {
                            const bankCurrency = (bank.base_currency?.currency || bank.currency?.currency || bank.currency || 'AED').toString().trim().toUpperCase();
                            if (bankCurrency === 'USD') {
                                const aedRateObj = bank.exchange_rates?.find(r =>
                                    (r.currency?.currency || r.currency || '').toString().trim().toUpperCase() === 'AED'
                                );
                                let usdAmount = parseFloat(item.amount);
                                if (aedRateObj) {
                                    const rate = parseFloat(aedRateObj.rate || aedRateObj.exchange_rate || 0);
                                    if (rate > 0) usdAmount = usdAmount * rate;
                                } else {
                                    const globalRate = parseFloat(localStorage.getItem('exchange_rate') || '3.67');
                                    usdAmount = usdAmount / globalRate; // Fallback
                                }
                                usdTooltipContent = `USD ${Math.abs(usdAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            }
                        }

                        const balanceSpan = (
                            <span className={item.dc}>
                                {formatAmountWithType(item.amount, item.dc)}
                            </span>
                        );

                        if (usdTooltipContent) {
                            return (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id={`tooltip-${item.name}`}>{usdTooltipContent}</Tooltip>}
                                >
                                    {balanceSpan}
                                </OverlayTrigger>
                            );
                        }

                        return balanceSpan;
                    })()}
                </div>

                {/* Children recursively */}
                {isGroup && (
                    <div className="ps-3 border-start">
                        {item.children.map((child, index) => (
                            <RenderAccount key={`${child.name}-${index}`} item={child} level={level + 1} parentName={item.name} />
                        ))}
                    </div>
                )}

                {/* Total for Groups */}
                {isGroup && (
                    <div className={`d-flex justify-content-between border-top ${level === 0 ? 'pt-2 mt-2' : 'pt-1 mt-1 text-muted'}`} style={{ fontSize: level === 0 ? 'inherit' : '0.85rem' }}>
                        {level === 0 ? (
                            <strong>Total {item.name}</strong>
                        ) : (
                            <span className="font-italic">Subtotal {item.name}</span>
                        )}
                        <strong className={item.dc}>
                            {formatAmountWithType(item.total || item.amount, item.dc)}
                        </strong>
                    </div>
                )}
            </div>
        );
    };
    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Financial Reports</h4>
                            </div>
                        </div>
                    </div>

                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="align-items-center mb-3">
                                <Col md={6}>
                                    <h2 className="mb-0">
                                        <FiCreditCard className="me-2" />
                                        Balance Sheet
                                    </h2>
                                    <p className="text-muted mb-0">Financial position overview</p>
                                </Col>
                                <Col md={6} className="text-end">
                                    <DropdownButton
                                        variant="outline-danger"
                                        title={<span><FiDownload className="me-2" />Export PDF</span>}
                                        id="export-dropdown"
                                        className="d-inline-block me-2"
                                    >
                                        <Dropdown.Item onClick={() => downloadFile('pdf', false)}>
                                            Groups Data (Standard)
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => downloadFile('pdf', true)}>
                                            All Data (Expanded)
                                        </Dropdown.Item>
                                    </DropdownButton>
                                    {/* <Button
                                        variant="outline-success"
                                        className="me-2"
                                        onClick={() => downloadFile('csv')}
                                    >
                                        <FiDownload className="me-2" />
                                        Export CSV
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => downloadFile('xls')}
                                    >
                                        <FiDownload className="me-2" />
                                        Export Excel
                                    </Button> */}
                                </Col>
                            </Row>

                            <Card className="mb-4">
                                <Card.Header className="bg-light">
                                    <h5 className="mb-0">
                                        <FiFilter className="me-2" />
                                        Filters
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-2">
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Date Range</Form.Label>
                                                <Form.Select
                                                    size="small"
                                                    value={dateRangeFilter}
                                                    onChange={(e) => handleDateRangeChange(e.target.value)}
                                                    className="font-size-11"
                                                >
                                                    <option value="yearly">Yearly</option>
                                                    <option value="custom">Custom Range</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        {dateRangeFilter === 'yearly' && (
                                            <>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Start Date</Form.Label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                views={["year", "month", "day"]}
                                                                openTo="year"
                                                                format="DD-MM-YYYY"
                                                                value={startDate ? dayjs(startDate) : null}
                                                                onChange={(newValue) => {
                                                                    if (newValue) {
                                                                        setSelectedYear(newValue);
                                                                        if (!startDate || dayjs(startDate).year() !== newValue.year()) {
                                                                            const start = newValue.startOf('year');
                                                                            setStartDate(start.format('YYYY-MM-DD'));
                                                                            setEndDate(newValue.endOf('year').format('YYYY-MM-DD'));
                                                                            setSelectedEndYear(newValue);
                                                                        } else {
                                                                            const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate);
                                                                            setStartDate(newStart);
                                                                            if (suggestedEndDate) {
                                                                                setEndDate(suggestedEndDate);
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                            />
                                                        </LocalizationProvider>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>End Date</Form.Label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                views={["year", "month", "day"]}
                                                                openTo="year"
                                                                format="DD-MM-YYYY"
                                                                value={endDate ? dayjs(endDate) : null}
                                                                onChange={(newValue) => {
                                                                    if (newValue) {
                                                                        setSelectedEndYear(newValue);
                                                                        if (!endDate || dayjs(endDate).year() !== newValue.year()) {
                                                                            setEndDate(newValue.endOf('year').format('YYYY-MM-DD'));
                                                                        } else {
                                                                            const dateStr = getAdjustedEndDate(newValue, startDate);
                                                                            setEndDate(dateStr);
                                                                        }
                                                                    }
                                                                }}
                                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                            />
                                                        </LocalizationProvider>
                                                    </Form.Group>
                                                </Col>
                                            </>
                                        )}

                                        {dateRangeFilter === 'custom' && (
                                            <>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>From Date</Form.Label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                views={["year", "month", "day"]}
                                                                format="DD-MM-YYYY"
                                                                value={startDate ? dayjs(startDate) : null}
                                                                onChange={(newValue) => {
                                                                        const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate);
                                                                        setStartDate(newStart);
                                                                        if (suggestedEndDate) {
                                                                            setEndDate(suggestedEndDate);
                                                                        }
                                                                }}
                                                                slotProps={{
                                                                    textField: { size: "small", fullWidth: true },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>To Date</Form.Label>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                views={["year", "month", "day"]}
                                                                format="DD-MM-YYYY"
                                                                value={endDate ? dayjs(endDate) : null}
                                                                onChange={(newValue) => {
                                                                        setEndDate(getAdjustedEndDate(newValue, startDate));
                                                                }}
                                                                slotProps={{
                                                                    textField: { size: "small", fullWidth: true },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </Form.Group>
                                                </Col>
                                            </>
                                        )}

                                        <Col md={4} className="d-flex align-items-end">
                                            <Button
                                                variant="primary"
                                                onClick={fetchBalanceSheet}
                                                disabled={loading}
                                                className="btn-sm"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Loading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiRefreshCw className="me-2" />
                                                        Apply
                                                    </>
                                                )}
                                            </Button>&nbsp;
                                            <Button
                                                variant="outline-secondary"
                                                onClick={handleClearFilters}
                                                disabled={loading}
                                                className="btn-sm"
                                            >
                                                <FiXCircle className="me-2" />
                                                Clear
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    {error}
                                </Alert>
                            )}

                            {data && (
                                <div className="balancesheet-content">
                                    <div className="report-header mb-4">
                                        <h4>Balance Sheet</h4>
                                        <p className="text-muted">
                                            {startDate && endDate ? (
                                                dayjs(startDate).isSame(dayjs(endDate).startOf('year'), 'day') && dayjs(endDate).isSame(dayjs(startDate).endOf('year'), 'day') ? (
                                                    `Jan ${dayjs(startDate).format('YYYY')} - Dec ${dayjs(endDate).format('YYYY')}`
                                                ) : (
                                                    `${dayjs(startDate).format('DD-MM-YYYY')} - ${dayjs(endDate).format('DD-MM-YYYY')}`
                                                )
                                            ) : (
                                                'All Time Period'
                                            )}
                                        </p>
                                    </div>

                                    <Row>
                                        {/* Assets Section */}
                                        <Col md={6}>
                                            <Card className="h-100">
                                                <Card.Header className="bg-light">
                                                    <h5 className="mb-0">Assets</h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    {data.assets.map((item, index) => (
                                                        <RenderAccount key={`${item.name}-${index}`} item={item} />
                                                    ))}
                                                    <div className="mt-4">
                                                        <div className="d-flex justify-content-between border-top pt-2">
                                                            <h5>Total Assets</h5>
                                                            {/* <h5>{formatCurrency(data.assets_total)}</h5> */}
                                                            <h5 className={(data.assets_dc)}>
                                                                {formatAmountWithType(data.assets_total, data.assets_dc)}
                                                            </h5>
                                                        </div>
                                                        {data.net_profit_loss < 0 && (
                                                            <div className="d-flex justify-content-between pt-2">
                                                                <h5>Profit & Loss Account (Net Loss)</h5>
                                                                {/* <h5 className="text-danger">{formatCurrency(Math.abs(data.net_profit_loss))}</h5> */}
                                                                <h5 className="text-danger">
                                                                    {formatAmountWithType(Math.abs(data.net_profit_loss), 'D')}
                                                                </h5>
                                                            </div>
                                                        )}
                                                        <div className="d-flex justify-content-between border-top pt-2 mt-2">
                                                            <h5>Total</h5>
                                                            <h5>{formatCurrency(data.final_assets_total)}</h5>
                                                            {/* <h5 className={getAmountClass(data.final_assets_dc)}>
                                                {formatAmountWithType(data.final_assets_total, data.final_assets_dc)}
                                            </h5> */}
                                                        </div>
                                                    </div>

                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* Liabilities Section */}
                                        <Col md={6}>
                                            <Card className="h-100">
                                                <Card.Header className="bg-light">
                                                    <h5 className="mb-0">Liabilities</h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    {data.liabilities.map((item, index) => (
                                                        <RenderAccount key={`${item.name}-${index}`} item={item} />
                                                    ))}
                                                    <div className="mt-4">
                                                        <div className="d-flex justify-content-between border-top pt-2">
                                                            <h5>Total Liabilities</h5>
                                                            {/* <h5>{formatCurrency(data.liabilities_total)}</h5> */}
                                                            <h5 className={(data.dc)}>
                                                                {formatAmountWithType(data.liabilities_total, data.dc)}
                                                            </h5>
                                                        </div>
                                                        <div className="d-flex justify-content-between pt-2">

                                                        </div>
                                                        {data.is_opening_difference && (
                                                            <div className="d-flex justify-content-between pt-2">
                                                                <div>Diff in O/P Balance</div>
                                                                {/* <h5>{formatCurrency(data.opening_balance_difference.opdiff_balance)}</h5> */}
                                                                <h5 className={(data.opening_balance_difference.opdiff_balance_dc)}>
                                                                    {formatAmountWithType(data.opening_balance_difference.opdiff_balance, data.opening_balance_difference.dc)}
                                                                </h5>
                                                            </div>
                                                        )}
                                                        <div className="d-flex justify-content-between border-top pt-2 mt-2">
                                                            <h5>Total</h5>
                                                            <h5>{formatCurrency(data.final_liabilities_total)}</h5>
                                                        </div>
                                                    </div>

                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Net Profit/Loss - Now shown in the same line */}
                                    <Card className="mt-4">
                                        <Card.Body className="d-flex justify-content-between align-items-center">
                                            <h4 className="mb-0">Net Profit/Loss</h4>
                                            <h4 className={`mb-0 ${data.raw_totals.net_profit_loss.direction === 'C' ? 'text-success' : 'text-danger'}`}>
                                                {formatAmountWithType(
                                                    data.raw_totals.net_profit_loss.amount,
                                                    data.raw_totals.net_profit_loss.direction
                                                )}
                                            </h4>
                                        </Card.Body>
                                    </Card>
                                </div>
                            )}

                            <LedgerDetailsOffcanvas
                                show={showOffcanvas}
                                onHide={() => setShowOffcanvas(false)}
                                ledger={selectedLedger}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Balancesheet;
